import { useMemo } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { Serie } from '@nivo/line'
import { GroupOption } from '@/types/metrics'
import { ByTimeMetricReport } from '@/types/workflow-metric-reports'
import {
  formatter,
  getBottomMarginForCountChart,
  getLeftMarginForCountChart,
} from '@/utils/chart'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import useTimeChartTicks from '@/hooks/useTimeChartTicks'
import LineChart from '@/components/charts/LineChart'
import LeftBorderedBox from '@/components/validation/LeftBorderedBox'
import { DateRange } from './ProjectMetricsDashboard'

export type ByTimeAndStateMetricLineChartProps = {
  type: 'count' | 'time'
  groupBy: GroupOption
  selectedDateRange: DateRange
  filteredStateIds?: string[]
  report?: ByTimeMetricReport
  height?: number
}

function getTime(time: number, units: 'min' | 'hrs' | 'days') {
  let updatedTime = time
  switch (units) {
    case 'min':
      updatedTime = time / 60
      break
    case 'hrs':
      updatedTime = time / 60 / 60
      break
    case 'days':
      updatedTime = time / 60 / 60 / 24
      break
  }
  return Number.isInteger(updatedTime) ? updatedTime : +updatedTime.toFixed(1)
}

function getTimeAndUnits(value: number): {
  value: string
  units: 'min' | 'hrs' | 'days'
} {
  let time = value / 60 // minutes

  if (time < 120) {
    return {
      value: `${Number.isInteger(time) ? time : time.toFixed(1)} min`,
      units: 'min',
    }
  }
  time = time / 60 // hours

  if (time < 48) {
    return {
      value: `${Number.isInteger(time) ? time : time.toFixed(1)} hrs`,
      units: 'hrs',
    }
  }
  time = time / 24 // days
  return {
    value: `${Number.isInteger(time) ? time : time.toFixed(1)} days`,
    units: 'days',
  }
}

export default function ByTimeAndStateMetricLineChart({
  type,
  report,
  groupBy,
  filteredStateIds,
  selectedDateRange,
  height,
}: ByTimeAndStateMetricLineChartProps) {
  const getThemeColor = useGetThemeColor()

  const { axisBottom, includeYear, needsToRotate, showingRange } =
    useTimeChartTicks({
      from: selectedDateRange.startDate,
      to: selectedDateRange.endDate,
      dataLength: report?.reports.length || 0,
      groupedBy: groupBy,
    })

  const {
    maxYValue,
    maxYValueUnits,
  }: {
    maxYValue: number | string
    maxYValueUnits: 'count' | 'min' | 'hrs' | 'days'
  } = useMemo(() => {
    let max: number | string = Math.max(
      ...(report?.reports.map(({ reports }) =>
        Math.max(
          ...reports.map((r) => {
            if (
              filteredStateIds &&
              !filteredStateIds.includes(r.workflow_state_id)
            ) {
              return 0
            }
            return r.formatted_result
          }),
        ),
      ) || [0]),
    )
    let units: 'count' | 'min' | 'hrs' | 'days' = 'count'
    if (type === 'time') {
      const time = getTimeAndUnits(max)
      max = time.value
      units = time.units
    }
    return { maxYValue: max, maxYValueUnits: units }
  }, [filteredStateIds, report?.reports, type])

  const groupedMetricReports = useMemo(
    () =>
      report?.reports.reduce<Record<string, { x: string; y: number }[]>>(
        (acc, { date, reports }) => {
          reports.forEach((r) => {
            if (
              filteredStateIds &&
              !filteredStateIds.includes(r.workflow_state_id)
            ) {
              return
            }
            const formattedValue =
              type === 'time' && maxYValueUnits != 'count'
                ? getTime(r.formatted_result, maxYValueUnits)
                : r.formatted_result
            acc[r.workflow_state_id] ??= []
            acc[r.workflow_state_id].push({ x: date, y: formattedValue })
          })
          return acc
        },
        {},
      ) || {},
    [filteredStateIds, maxYValueUnits, report?.reports, type],
  )

  const data: Serie[] = useMemo(
    () =>
      report?.reports[0]?.reports.map(
        ({
          workflow_state_id,
          workflow_state__name,
          workflow_state__color,
        }) => {
          return {
            id: workflow_state__name,
            name: workflow_state__name,
            color: getThemeColor(workflow_state__color),
            data: groupedMetricReports[workflow_state_id] || [],
          }
        },
      ) || [],
    [report, getThemeColor, groupedMetricReports],
  )

  return (
    // Not using xScale type time since how many ticks we show depend on the data and custom range
    // also, it doesn't support week precision
    <LineChart
      animate={false} // Disabling animation since lines get "stuck" in "old" places when updating group or time period
      data={data}
      height={height || 250}
      dataHasColor
      pointSize={10}
      margin={{
        bottom: getBottomMarginForCountChart(
          needsToRotate,
          includeYear,
          showingRange,
        ),
        left: getLeftMarginForCountChart(maxYValue),
        right: 20,
      }}
      xFormat={(value) =>
        formatter(
          value as string,
          groupBy,
          includeYear,
          selectedDateRange.endDate,
        )
      }
      axisBottom={axisBottom}
      yFormat={type === 'time' ? (v) => `${v} ${maxYValueUnits}` : undefined}
      tooltipContent={(point, stackedPoints) => {
        return (
          <Stack sx={{ width: 'max-content', maxWidth: 300 }}>
            {stackedPoints.map((stackedPoint) => (
              <Box key={stackedPoint.id} sx={{ position: 'relative' }}>
                <LeftBorderedBox
                  color={stackedPoint.color || point.color}
                  sx={{
                    p: 1,
                    pl: 1.75,
                    display: 'flex',
                    justifyContent: 'space-between',
                    columnGap: 1,
                  }}
                >
                  <Typography variant="body2" noWrap>
                    {stackedPoint.id}
                  </Typography>
                  <Typography variant="body2" sx={{ flexShrink: 0 }}>
                    <b>{point.data.yFormatted}</b>
                  </Typography>
                </LeftBorderedBox>
              </Box>
            ))}
          </Stack>
        )
      }}
    />
  )
}
